import React from "react";

import { Picto, useMediaQuery } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { NavLink, useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useRouterSearchParams } from "@/hooks/useRouterSearchParams";

import { Avatar } from "@/components/atoms";
import { Menu, NavBar } from "@/components/organisms";
import { EmailVerificationBanner } from "@/components/organisms/EmailVerificationBanner/EmailVerificationBanner";
import { SubscriptionBanner } from "@/components/organisms/SubscriptionBanner/SubscriptionBanner";
import { UpgradeModal } from "@/components/organisms/UpgradeModal/UpgradeModal";

import { cn } from "@/utils/cn";

import { MOBILE_MEDIA_QUERY } from "@/constants/CConstants";
import { CHRONOS_FILTERS } from "@/constants/CFilters";
import { UserRole } from "@/constants/CRoles";

import { FiltersProvider } from "@/hooks";
import {
	VIEWS_PATHS,
	HOME_PATHS,
	NavBarRoutes,
	PROJECTS_PATHS,
} from "@/router/NavRoutes";

interface ILayoutProps {
	children: React.ReactNode;
}

export const DashboardLayout: React.FC<ILayoutProps> = ({ children }) => {
	const [isReduced, setIsReduced] = React.useState(false);
	const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
	const navigate = useNavigate();
	const { hasRole } = useAuth();
	// Off-plan entries stay in the menu with a lock: the page explains.
	const pdf = usePlanGate("pdf");
	const invoicing = usePlanGate("invoices");
	// Paused features leave the menu (feature_flags).
	const invoicesOn = useFeatureFlag("invoices");
	const sharingOn = useFeatureFlag("sharing");
	const searchParamsAdapter = useRouterSearchParams();

	return (
		<FiltersProvider
			defaultFilters={CHRONOS_FILTERS}
			adapterHook={searchParamsAdapter}
		>
			<div
				className={cn([
					"flex h-full w-full",
					isMobile && "flex-col-reverse",
				])}
			>
				{!isMobile ? (
					<div
						data-tour="menu"
						className={cn([
							"relative m-2 flex w-64 flex-col items-center gap-8 rounded-lg p-2 shadow-sm duration-300",
							isReduced && "w-16",
						])}
					>
						<div
							className="mx-auto mb-8 flex cursor-pointer items-center gap-2 py-1 text-2xl whitespace-nowrap"
							role="button"
							onClick={() => navigate("/")}
						>
							<img
								src="/assets/logo.svg"
								alt="Logo"
								className="h-8 w-8"
							/>
							{!isReduced && (
								<span className="leading-none tracking-[0.2em] uppercase">
									{t("global.appName")}
								</span>
							)}
						</div>
						<button
							className="absolute top-14 right-0 z-10 aspect-square translate-x-1/2 cursor-pointer overflow-hidden rounded-full bg-white p-1 shadow-sm"
							onClick={() => setIsReduced((prev) => !prev)}
						>
							<Picto
								icon="menu"
								className="h-5 w-5 text-neutral-300"
								rotation={isReduced ? -90 : 0}
							/>
						</button>
						<div className="w-full overflow-auto">
							<Menu
								isReduced={isReduced}
								routes={[
									{
										name: "home",
										title: t("global.home"),
										picto: "home",
										path: HOME_PATHS,
									},
									{
										name: "search",
										title: t("search.placeholder"),
										picto: "search",
										path: "/search",
									},
									{
										name: "projects",
										title: t("projects.title"),
										picto: "folder",
										path: PROJECTS_PATHS,
									},
									{
										name: "views",
										title: t("views.title"),
										picto: "calendar",
										path: VIEWS_PATHS,
									},
									{
										name: "reports",
										title: t("reports.title"),
										picto: "file",
										path: "/reports",
										locked: pdf.locked,
									},
									{
										name: "invoices",
										title: t("invoices.title"),
										picto: "fileText",
										path: "/invoices",
										locked: invoicing.locked,
										hidden: !invoicesOn,
									},
									{
										name: "companies",
										title: t("company.title"),
										path: ["/companies"],
										picto: "briefcase",
									},
									{
										name: "clients",
										title: t("client.title"),
										picto: "target",
										path: "/clients",
									},
									{
										name: "workspace",
										title: t("workspace.title"),
										picto: "users",
										path: "/workspace",
										hidden: !sharingOn,
									},
									{
										name: "admin",
										title: t("admin.title"),
										picto: "settings",
										path: "/admin/users",
										hidden: !hasRole([UserRole.ADMIN]),
									},
								]}
							/>
						</div>
						<div className="mt-auto w-full">
							<Footer isReduced={isReduced} />
						</div>
					</div>
				) : (
					<NavBar routes={NavBarRoutes} />
				)}
				{/* Banners take their own height above the page, which gets
				    the rest: a full-height page (Home) no longer overflows
				    by the banner height into a second scrollbar. */}
				<div className="flex min-h-0 min-w-0 flex-1 flex-col">
					<EmailVerificationBanner />
					<SubscriptionBanner />
					<div className="dashboard__scrollable-cont-parent relative min-h-0 flex-1 overflow-auto">
						{children}
					</div>
				</div>
			</div>
			<UpgradeModal />
		</FiltersProvider>
	);
};

interface IFooterProps {
	isReduced?: boolean;
}

const Footer: React.FC<IFooterProps> = ({ isReduced }) => {
	const { user, logout } = useAuth();
	if (!user) return null;

	return (
		<NavLink
			to="/profile"
			className={cn([
				"flex w-full items-center justify-start gap-2 overflow-hidden no-underline!",
				isReduced && "flex-col-reverse justify-center gap-4",
			])}
		>
			<Avatar url={user?.avatar} className="h-10 w-10 shrink-0" />
			{!isReduced ? (
				<div className="flex flex-col">
					<span className="font-bold">{user?.firstName}</span>
					<LogoutButton logout={logout} />
				</div>
			) : (
				<LogoutButton isReduced={isReduced} logout={logout} />
			)}
		</NavLink>
	);
};

const LogoutButton: React.FC<{ isReduced?: boolean; logout: () => void }> = ({
	isReduced,
	logout,
}) => {
	return (
		<button
			className="group flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 hover:underline"
			// Icon only once the sidebar is reduced: the name stays for
			// screen readers and as a tooltip.
			aria-label={isReduced ? t("auth.logout") : undefined}
			title={isReduced ? t("auth.logout") : undefined}
			onClick={(e) => {
				e.stopPropagation();
				e.preventDefault();
				logout();
			}}
		>
			<Picto
				icon="logout"
				className={cn([
					isReduced ? "h-5 w-5" : "h-4 w-4",
					"group-hover:text-red-600",
				])}
			/>
			{!isReduced && t("auth.logout")}
		</button>
	);
};
